import * as React from "react";
import { Button } from "../../../bits/Button/Button";
import { useHistory } from "react-router-dom";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../../utils/intakeEmailSignature";
import { useToasts } from "react-toast-notifications";
import {
    ConsentForm,
    DateFormats,
    IClient,
    IClientBooking,
    IClinician,
    SignatureData,
    Template,
} from "intake_shared/src/types";
import { getClinicianByEmail } from "../../../../../store/clinician";
import {
    sendAppointmentConfirmedEmails,
    sendAppointmentHeldEmails,
    sendClientTerminationEmails,
    sendCHPCareerAssessmentFormEmails,
    sendCHPExternTherapyConsentFormEmails,
    sendCHPNutritionCounselingFormEmails,
    sendCHPPsychFormEmails,
    sendCHPTestingConsentFormEmails,
    sendHumantoldConsentFormEmails,
    sendPH_IN_NetworkConsentFormEmails,
    sendPHNutritionCounselingConsentFormEmail,
    sendKAPConsentFormEmails,
} from "../../../../../utils/queue_email";
import {
    findLabelByPracticeId,
    getConfirmedEmailToSend,
    toTitleCase,
} from "../../../../../utils/utils";
import moment from "../../../../../utils/app_moment";
import Table from "../../../bits/Table/Table";
import { AlertDialog } from "../../../bits/AlertDialog/AlertDialog";
import AppointmentEmail from "../AppointmentEmail/AppointmentEmail";
import ConsentEmail from "../ConsentEmail/ConsentEmail";
import EmptyListState from "../../../bits/EmptyListState/EmptyListState";
import {
    getAppointmentConsentForm,
    getTemplateConsentForm,
} from "../../../../../utils/consent_form";

interface TemplateListProps {
    templates: Template[];
    onRefresh?: () => {};
    client?: IClient;
}

export default function TemplateList({
    templates,
    onRefresh,
    client,
}: TemplateListProps) {
    const { push } = useHistory();
    const olc = React.useContext(CacheContext);
    const { addToast } = useToasts();
    const [showDialog, setShowDialog] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [showEmailModal, setShowEmailModal] = React.useState({
        show: false,
        type: "",
    });
    const intakeSignature = getIntakeEmailSignature(olc);
    const [showConsentEmailModal, setShowConsentEmailModal] = React.useState({
        show: false,
        type: "",
    });
    const [modalContent, setModalContent] = React.useState<{
        title: string;
        body: JSX.Element;
        onConfirm: () => void;
    } | null>(null);
    const allDepartmentsAndServices = olc?.allDepartmentAndServices || {};
    const allConsentForms = olc?.consentForms || {};

    const sendAppointmentEmail = async (id: string, option?: string) => {
        let selectedAppt;
        if (showEmailModal.type === "appointment_confirmed_clinician_template") {
            selectedAppt = client?.confirmed_bookings.find((item) => item.id === id);
        }

        if (showEmailModal.type === "appointment_held_clinician_consult_template") {
            const allBookings = [
                ...(client?.held_bookings ?? []),
                ...(client?.confirmed_bookings ?? []),
            ];
            selectedAppt = allBookings.find((item) => item.id === id);
        }
        if (!selectedAppt) {
            throw new Error(`Appointment not found for id ${id}`);
        }
        const clinicianId = selectedAppt.clinician_id?.trim();
        if (!clinicianId) {
            throw new Error(`Clinician id not found for appointment id ${id}`);
        }
        const clinician = await getClinicianByEmail(clinicianId);
        if (!clinician) {
            throw new Error(
                `Clinician record not found for clinician id ${clinicianId}`
            );
        }

        const signatureEmail =
            client?.assigned_to || client?.secondary_assignee || "";

        const allEmailSignatures = olc?.emailSignatures || {};
        const signature = allEmailSignatures[signatureEmail as string];
        const locationAddress = olc?.location_address?.[selectedAppt.location || ""];

        let supervisor: IClinician | undefined;
        if (clinician?.supervisor_id) {
            supervisor = await getClinicianByEmail(clinician?.supervisor_id);
        }
        const supervisor_name =
            `${supervisor?.firstname || ""} ${supervisor?.lastname || ""}` ||
            `${supervisor?.fullname || ""}` ||
            "";

        const appointmentPractice = findLabelByPracticeId(
            selectedAppt?.practice_id ||
                (process.env.REACT_APP_MANTLE_PRACTICE_ID as string),
            allDepartmentsAndServices
        );
        const form =
            getAppointmentConsentForm(
                appointmentPractice || "",
                selectedAppt,
                allConsentForms
            ) || ({} as ConsentForm);

        if (
            showEmailModal.type === "appointment_confirmed_clinician_template" &&
            option === "send_clinician_only"
        ) {
            await sendAppointmentConfirmedEmails(
                client as IClient,
                clinician as IClinician,
                selectedAppt as IClientBooking,
                allDepartmentsAndServices,
                intakeSignature || ({} as SignatureData),
                locationAddress || ""
            );
        }
        if (
            showEmailModal.type === "appointment_confirmed_clinician_template" &&
            option === "send_client_only"
        ) {
            const department = findLabelByPracticeId(
                selectedAppt?.practice_id ||
                    (process.env.REACT_APP_MANTLE_PRACTICE_ID as string),
                allDepartmentsAndServices
            );

            getConfirmedEmailToSend(
                "client_and_clinician_confirm",
                client as IClient,
                clinician as IClinician,
                selectedAppt as IClientBooking,
                allDepartmentsAndServices,
                signature,
                supervisor_name ? supervisor_name : "",
                selectedAppt.requested_service || "",
                selectedAppt.service_delivery_option as string,
                department || "",
                locationAddress || "",
                "template",
                form,
                intakeSignature || ({} as SignatureData)
            );
        }

        if (showEmailModal.type === "appointment_held_clinician_consult_template") {
            await sendAppointmentHeldEmails(
                client as IClient,
                clinician as IClinician,
                selectedAppt as IClientBooking,
                true,
                intakeSignature
            );
        }
    };

    const sendConsentEmail = async (id: string) => {
        if (!client) {
            throw new Error("Client not found for consent email");
        }

        const allBookings = [...client.held_bookings, ...client.confirmed_bookings];
        const selectedAppt = allBookings.find((item) => item.id === id);

        if (!selectedAppt) {
            throw new Error("No appointment found for consent email");
        }

        const signatureEmail = client.assigned_to || client.secondary_assignee || "";
        const allEmailSignatures = olc?.emailSignatures || {};
        const signature = allEmailSignatures[signatureEmail as string];
        const form = getTemplateConsentForm(
            showConsentEmailModal.type,
            allConsentForms
        );

        const clinicianId = selectedAppt.clinician_id?.trim();
        const clinician = clinicianId
            ? await getClinicianByEmail(clinicianId)
            : null;
        const options = { clinicianCredential: clinician?.credential ?? "" };

        switch (showConsentEmailModal.type) {
            case "humantold_consent_form":
                await sendHumantoldConsentFormEmails(
                    client,
                    selectedAppt,
                    form,
                    signature,
                    options
                );
                break;
            case "parkhill_consent_form":
                await sendPH_IN_NetworkConsentFormEmails(
                    client,
                    selectedAppt,
                    form,
                    signature,
                    options
                );
                break;
            case "parkhill_nutrition_consent_form":
                await sendPHNutritionCounselingConsentFormEmail(
                    client,
                    selectedAppt,
                    form,
                    signature,
                    options
                );
                break;
            case "chp_testing_consent_form":
                await sendCHPTestingConsentFormEmails(
                    client,
                    selectedAppt,
                    form,
                    signature,
                    options
                );
                break;
            case "kap_consent":
                await sendKAPConsentFormEmails(
                    client,
                    selectedAppt,
                    form,
                    signature,
                    options
                );
                break;
            case "chp_psychiatry_consent_form":
                await sendCHPPsychFormEmails(
                    client,
                    selectedAppt,
                    form,
                    signature,
                    options
                );
                break;
            case "chp_nutrition_counseling_consent_form":
                await sendCHPNutritionCounselingFormEmails(
                    client,
                    selectedAppt,
                    form,
                    signature,
                    options
                );
                break;
            case "chp_therapy_consent_form":
                await sendCHPExternTherapyConsentFormEmails(
                    client,
                    selectedAppt,
                    form,
                    signature,
                    options
                );
                break;
            case "chp_career_assessment_consent_form":
                await sendCHPCareerAssessmentFormEmails(
                    client,
                    selectedAppt,
                    form,
                    signature,
                    options
                );
                break;
            default:
                break;
        }
    };

    const handleEmailConfirm = async (appointmentId, option) => {
        setLoading(true);
        try {
            await sendAppointmentEmail(appointmentId, option);
            addToast("Email sent successfully", { appearance: "success" });
            setShowEmailModal({
                show: false,
                type: "",
            });
        } catch (error) {
            console.error(error);
            addToast("An error occurred", { appearance: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleConsentEmailConfirm = async (appointmentId) => {
        setLoading(true);
        try {
            await sendConsentEmail(appointmentId);
            addToast("Consent email sent successfully", { appearance: "success" });
            setShowConsentEmailModal({
                show: false,
                type: "",
            });
        } catch (error) {
            console.error(error);
            addToast(error instanceof Error ? error.message : "An error occurred", {
                appearance: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAction = (name: string, subject: string, templateId: string) => {
        switch (name) {
            case "client_termination_email_template":
                setModalContent({
                    title: "Send Client Termination Email",
                    body: <div>Are you sure you want to send out this email?</div>,
                    onConfirm: async () => {
                        setLoading(true);
                        try {
                            const signatureEmail =
                                client?.assigned_to ||
                                client?.secondary_assignee ||
                                "";
                            const allEmailSignatures = olc?.emailSignatures || {};
                            const signature =
                                allEmailSignatures[signatureEmail as string];
                            await sendClientTerminationEmails(
                                client as IClient,
                                {} as IClientBooking,
                                signature
                            );
                            addToast(`Email sent successfully`, {
                                appearance: "success",
                            });
                            setShowDialog(false);
                        } catch (err) {
                            console.error(err);
                            addToast(`An error occurred`, { appearance: "error" });
                        } finally {
                            setLoading(false);
                        }
                    },
                });
                setShowDialog(true);
                break;
            case "appointment_held_clinician_consult_template":
                setShowEmailModal({
                    show: true,
                    type: "appointment_held_clinician_consult_template",
                });
                break;
            case "appointment_confirmed_clinician_template":
                setShowEmailModal({
                    show: true,
                    type: "appointment_confirmed_clinician_template",
                });
                break;
            case "parkhill_consent_form":
            case "parkhill_nutrition_consent_form":
            case "humantold_consent_form":
            case "chp_testing_consent_form":
            case "kap_consent":
            case "chp_psychiatry_consent_form":
            case "chp_nutrition_counseling_consent_form":
            case "chp_therapy_consent_form":
            case "chp_career_assessment_consent_form":
                setShowConsentEmailModal({
                    show: true,
                    type: name,
                });
                break;
            default:
                return;
        }
    };

    const columns = React.useMemo(() => {
        return [
            {
                Header: "Name",
                accessor: (row: Template) => (
                    <p>
                        {row.name === "appointment_confirmed_clinician_template"
                            ? "Appointment Confirmed email template"
                            : toTitleCase(row?.name?.replaceAll("_", " "))}
                    </p>
                ),
            },
            {
                Header: "Updated at",
                accessor: (row: Template) => (
                    <p>
                        {`${moment(row?.updated_at as any)?.format(
                            DateFormats.DAY_HOUR_MIN_FORMAT
                        )}`}
                    </p>
                ),
            },
            {
                Header: "",
                id: "trigger",
                accessor: (row: Template) => (
                    <Button
                        onClick={() => {
                            handleAction(
                                row?.name,
                                row?.versions[0]?.subject,
                                row.id
                            );
                        }}
                    >
                        Send
                    </Button>
                ),
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addToast, push, onRefresh]);

    return (
        <>
            <AlertDialog
                open={showDialog}
                onClose={() => setShowDialog(false)}
                title={modalContent ? modalContent.title : ""}
                description={modalContent?.body}
                onSaveClick={modalContent?.onConfirm}
                onCancelClick={() => setShowDialog(false)}
                isSaveBtnDisabled={loading}
                saveText="Yes, Send Email"
            />
            <AppointmentEmail
                client={client}
                show={showEmailModal.show}
                onClose={() =>
                    setShowEmailModal({
                        show: false,
                        type: "",
                    })
                }
                onConfirm={handleEmailConfirm}
                loading={loading}
                type={showEmailModal.type}
            />
            <ConsentEmail
                client={client}
                show={showConsentEmailModal.show}
                onClose={() =>
                    setShowConsentEmailModal({
                        show: false,
                        type: "",
                    })
                }
                onConfirm={handleConsentEmailConfirm}
                loading={loading}
            />
            {templates && templates.length > 0 ? (
                <Table
                    columns={columns}
                    allowSelect={false}
                    data={templates || []}
                />
            ) : (
                <EmptyListState
                    emptyMessage="No templates found"
                    stateHelperText="Email templates will be found here when added"
                />
            )}
        </>
    );
}
